import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import { TextoDaIA } from "../componentes/TextoDaIA";
import {
  Destaque,
  Fonte,
  LinhaDoTempo,
  ListaDeAlertas,
  Marca,
  Secao,
  BalaoDoAssistente,
} from "../componentes/LeitorUi";
import {
  ARQUIVOS_DO_EXEMPLO,
  CHECKLIST_DO_EXEMPLO,
  CONVERSA_DO_EXEMPLO,
  PAGINAS_DO_EXEMPLO,
  RESULTADO_DO_EXEMPLO,
  TITULO_DO_EXEMPLO,
} from "../componentes/LeitorExemplo";
import { comMilhar } from "../lib/processos";

// A DEMONSTRAÇÃO — `/ferramenta/leitor-de-processos/exemplo`.
//
// Victor pediu em 02/09/2026 que o "Ver demonstração" da origem entrasse. Ele
// mostra o que a ferramenta devolve antes de o perito subir o processo de um
// cliente num sistema que ele ainda não conhece.
//
// ---------------------------------------------------------------------------
// A tela é a MESMA do resultado real, com três coisas desligadas e DITAS
// ---------------------------------------------------------------------------
// Os componentes são os do resultado (`Secao`, `LinhaDoTempo`,
// `ListaDeAlertas`, `TextoDaIA`), porque uma demonstração desenhada por uma
// tela paralela mostra uma ferramenta que não existe — e é o tipo de divergência
// que ninguém nota até o aluno reclamar de algo que "estava diferente".
//
// O que muda, e cada um DIZ o porquê em vez de simplesmente não funcionar:
//
//   1. a faixa de EXEMPLO, permanente e não configurável (a doutrina do
//      Simulador MEI: fidelidade visual pede o inverso na mesma medida);
//   2. o checklist aparece e não grava — não há análise a que pertencer;
//   3. o assistente mostra uma resposta pronta e o campo está desligado.
//
// ---------------------------------------------------------------------------
// Por que `exemplo` é segmento reservado, e não uma análise no banco
// ---------------------------------------------------------------------------
// Guardar uma análise "de todos" abriria a primeira exceção à RLS de dono desta
// ferramenta (`aluno_id = meu_profile_id()`), para sempre, por causa de uma
// tela de demonstração. O porquê está no cabeçalho de `LeitorExemplo.tsx`.
//
// `exemplo` e `nova` são as duas palavras reservadas do despacho, e nenhuma
// colide com o outro segmento possível — um UUID.
export default function LeitorDemonstracao() {
  const [busca] = useSearchParams();
  const curso = busca.get("curso");
  const q = curso ? `?curso=${encodeURIComponent(curso)}` : "";

  // O balão do assistente, igual ao do resultado real: nasce fechado.
  const [assistente, setAssistente] = useState(false);
  const [aba, setAba] = useState<"relatorio" | "linha-do-tempo" | "checklist" | "alertas">(
    "relatorio",
  );

  const r = RESULTADO_DO_EXEMPLO.relatorio!;
  const nomes = ARQUIVOS_DO_EXEMPLO.map((a) => a.nome);

  // Aqui a aba é `useState`, e não sub-rota como no resultado real — e a razão é
  // a diferença entre as duas telas: no resultado, o link de uma aba é algo que
  // o perito manda a um colega sobre o processo DELE. Uma demonstração não tem
  // esse uso; o que se compartilha é a demonstração inteira.
  const abas = [
    { chave: "relatorio", rotulo: "Relatório" },
    { chave: "linha-do-tempo", rotulo: "Linha do tempo" },
    { chave: "checklist", rotulo: "Checklist" },
    { chave: "alertas", rotulo: "Alertas" },
  ] as const;

  return (
    <>
      {/* A FAIXA. Ela vem ANTES da cabine e não sai da tela — quem chega por um
          link direto tem de saber, na primeira linha, que o processo não existe. */}
      <p className="rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-4 text-sm text-alerta-tinta">
        <strong className="font-semibold">Esta é uma demonstração.</strong> O processo abaixo
        não existe: número, partes e valores foram inventados para mostrar o que a ferramenta
        devolve. Nenhum documento foi analisado, e nada aqui é salvo.
      </p>

      <div className="mt-4">
        <Cabine
          trilha={{ para: `/ferramenta/leitor-de-processos${q}`, rotulo: "Leitor de Processos" }}
          rotulo="Demonstração"
          titulo={TITULO_DO_EXEMPLO}
          descricao={r.identificacao}
          acao={
            <Link to={`/ferramenta/leitor-de-processos/nova${q}`} className="pill-cta inline-flex">
              Analisar um processo de verdade
            </Link>
          }
          fatos={
            <>
              <TileDeFato
                rotulo="Páginas lidas"
                valor={comMilhar(PAGINAS_DO_EXEMPLO)}
                apoio={`em ${ARQUIVOS_DO_EXEMPLO.length} arquivos`}
              />
              <TileDeFato
                rotulo="Checklist"
                valor={`0 de ${CHECKLIST_DO_EXEMPLO.length}`}
                apoio="na sua análise, o que marcar fica salvo"
              />
              <TileDeFato
                rotulo="Alertas"
                valor={comMilhar(RESULTADO_DO_EXEMPLO.alertas?.length ?? 0)}
                apoio="pontos de atenção"
              />
            </>
          }
        />
      </div>

      <nav aria-label="Seções da demonstração" className="mt-6 flex flex-wrap gap-2">
        {abas.map((x) => (
          <button
            key={x.chave}
            type="button"
            onClick={() => setAba(x.chave)}
            aria-pressed={aba === x.chave}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              aba === x.chave
                ? "bg-azul text-white"
                : "border-[1.5px] border-borda text-tinta hover:border-laranja"
            }`}
          >
            {x.rotulo}
          </button>
        ))}
      </nav>

      <div className="mt-4">
        {aba === "relatorio" && (
          <div className="grid gap-4">
            <Secao titulo="Documentos analisados">
              <ul className="grid gap-2">
                {(r.documentosAnalisados ?? []).map((d, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm">
                    <span className="font-medium text-tinta">{d.nome}</span>
                    {d.fase && (
                      <span className="rounded-full bg-chip px-2 py-0.5 text-[0.6875rem] font-semibold text-chip-tinta">
                        {d.fase}
                      </span>
                    )}
                    {d.classe && <span className="text-xs text-cinza">{d.classe}</span>}
                    {d.paginas && <span className="text-xs text-cinza">págs. {d.paginas}</span>}
                  </li>
                ))}
              </ul>
            </Secao>

            <Secao titulo="Identificação do processo" paraCopiar={r.identificacao}>
              <Destaque texto={r.identificacao} />
            </Secao>
            <Secao titulo="Objeto da ação" paraCopiar={r.objetoAcao}>
              <Destaque texto={r.objetoAcao} />
            </Secao>
            <Secao titulo="Objeto da perícia" paraCopiar={r.objetoPericia}>
              <Destaque texto={r.objetoPericia} />
            </Secao>

            <Secao titulo="Pontos controvertidos">
              <ul className="grid gap-2">
                {(r.pontosControvertidos ?? []).map((p, i) => {
                  const texto = typeof p === "string" ? p : (p.texto ?? "");
                  const fonte = typeof p === "string" ? undefined : p.fonte;
                  return (
                    <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm text-tinta">
                      <span className="flex-1">
                        <Marca texto={texto} termo="" />
                      </span>
                      <Fonte nome={fonte} nomes={nomes} />
                    </li>
                  );
                })}
              </ul>
            </Secao>

            <Secao titulo="Quesitos">
              <ol className="grid gap-2">
                {(r.quesitos ?? []).map((qq, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm text-tinta">
                    <span className="font-mono text-xs text-cinza tabular-nums">{i + 1}.</span>
                    <span className="flex-1">{qq.texto}</span>
                    <Fonte nome={qq.fonte} nomes={nomes} />
                    {qq.pagina && (
                      <span className="text-xs whitespace-nowrap text-cinza">{qq.pagina}</span>
                    )}
                  </li>
                ))}
              </ol>
            </Secao>

            <Secao titulo="Documentos relevantes">
              <ul className="grid gap-2">
                {(r.documentosRelevantes ?? []).map((d, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm text-tinta">
                    <span className="flex-1">{d.nome}</span>
                    <Fonte nome={d.fonte} nomes={nomes} />
                    {d.pagina && (
                      <span className="text-xs whitespace-nowrap text-cinza">{d.pagina}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Secao>
          </div>
        )}

        {aba === "linha-do-tempo" && (
          <LinhaDoTempo eventos={RESULTADO_DO_EXEMPLO.linhaDoTempo ?? []} />
        )}

        {aba === "checklist" && (
          <div className="grid gap-3">
            {/* A caixa é DESLIGADA e a linha diz por quê. Deixá-la clicável sem
                gravar reproduziria de propósito o defeito central da origem, onde
                o tique não ia a lugar nenhum e o contador ficava preso em 0/N. */}
            <p className="rounded-cf border border-borda bg-chip/40 p-3 text-xs text-cinza">
              Nesta demonstração o checklist não grava — ele não pertence a nenhuma análise. Na
              sua, cada item marcado fica salvo, com a data.
            </p>
            <ul className="grid gap-2">
              {CHECKLIST_DO_EXEMPLO.map((i, n) => (
                <li key={n} className="surface rounded-cf p-3">
                  <span className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled
                      aria-label={`${i.texto} (desligado na demonstração)`}
                      className="mt-0.5 size-4"
                      readOnly
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-tinta">{i.texto}</span>
                      {i.pagina && <span className="text-xs text-cinza">{i.pagina}</span>}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {aba === "alertas" && <ListaDeAlertas alertas={RESULTADO_DO_EXEMPLO.alertas ?? []} />}
      </div>

      <section className="surface mt-6 rounded-cf p-5">
        <h2 className="font-display text-lg font-bold text-titulo">
          E o assistente fica no canto da tela
        </h2>
        <p className="mt-2 text-sm text-tinta">
          Aqui em cima o botão <strong className="font-semibold">Ver o assistente</strong> abre
          a conversa sem sair da aba que você está lendo. Na sua análise ele responde a partir
          do texto dos PDFs que você enviou e cita a página de cada trecho; se a informação não
          estiver lá, diz que não encontrou — em vez de supor.
        </p>

        <Link
          to={`/ferramenta/leitor-de-processos/nova${q}`}
          className="pill-cta mt-4 inline-flex"
        >
          Analisar um processo de verdade
        </Link>
      </section>

      {/* O balão é a MESMA peça do resultado real, com o campo desligado e
          dizendo por quê — demonstração desenhada por tela paralela mostra uma
          ferramenta que não existe. */}
      <BalaoDoAssistente
        aberto={assistente}
        aoTrocar={setAssistente}
        titulo="Assistente do processo"
        apoio="exemplo de resposta — este é um processo fictício"
        selo={CONVERSA_DO_EXEMPLO.filter((f) => f.papel === "user").length}
        rotuloFechado="Ver o assistente"
        rodape={
          <p className="text-xs text-cinza">
            O campo de pergunta está desligado nesta demonstração. Analise um processo de
            verdade para conversar sobre ele.
          </p>
        }
      >
        <div className="grid gap-3">
          {CONVERSA_DO_EXEMPLO.map((f, i) => (
            <div
              key={i}
              className={`rounded-cf px-3.5 py-2.5 text-sm ${
                f.papel === "user"
                  ? "ml-auto max-w-[88%] bg-chip text-chip-tinta"
                  : "border border-borda bg-superficie-2"
              }`}
            >
              {f.papel === "user" ? (
                <p className="whitespace-pre-wrap">{f.conteudo}</p>
              ) : (
                <TextoDaIA texto={f.conteudo} />
              )}
            </div>
          ))}
        </div>
      </BalaoDoAssistente>
    </>
  );
}
